import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import { useSelector } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";
import { getMemberships, getMembershipTypes } from "../../utils/membershipApi";
import { exportToCsv } from "../../utils/exportCsv";
import AssignMembershipModal from "./AssignMembershipModal";
import { STATUS_LABELS, formatDiscount } from "./membershipConstants";

/**
 * The members list — "find a person, see their card, go to their record".
 *
 * Search is one box across card number, employee id, name, phone and email, because that
 * is how the question actually arrives: somebody reads out whatever they have to hand.
 * The API does the OR across both tables in a single query.
 */

const buttonStyle = {
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    border: "none", cursor: "pointer", transition: "all 0.15s",
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)", textDecoration: "none",
    display: "inline-flex", alignItems: "center", gap: 6,
};

const ghostButton = {
    padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const inputStyle = {
    padding: "9px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, color: C.text, background: C.white, minWidth: 150,
};

export const STATUS_COLORS = {
    active: { color: C.green, bg: C.greenLight },
    pending: { color: C.orange, bg: C.orangeLight },
    suspended: { color: C.orange, bg: C.orangeLight },
    expired: { color: C.textMed, bg: C.borderLight },
    revoked: { color: C.coral, bg: C.coralLight },
};

export const StatusBadge = ({ status }) => {
    const s = STATUS_COLORS[status] || { color: C.textMed, bg: C.borderLight };
    return <Badge color={s.color} bg={s.bg}>● {STATUS_LABELS[status] || status}</Badge>;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const memberName = (m) =>
    [m?.user?.firstName, m?.user?.lastName].filter(Boolean).join(" ") || "—";

const MembershipList = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const merchantFilter = userType === "merchant"
        ? merchantID
        : (selectedMerchant && selectedMerchant !== "all") ? selectedMerchant : null;

    const [data, setData] = useState({ rows: [], total: 0, totalPages: 0 });
    const [types, setTypes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showAssign, setShowAssign] = useState(false);

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [filters, setFilters] = useState({ status: "", membershipTypeId: "", expiringInDays: "", search: "" });
    // Typing in the search box must not fire a request per keystroke.
    const [searchInput, setSearchInput] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters((f) => (f.search === searchInput ? f : { ...f, search: searchInput }));
            setPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const fetchData = useCallback(async () => {
        if (!merchantFilter) { setData({ rows: [], total: 0, totalPages: 0 }); return; }
        setIsLoading(true);
        setData(await getMemberships({
            merchantId: merchantFilter,
            page,
            limit,
            status: filters.status || undefined,
            membershipTypeId: filters.membershipTypeId || undefined,
            expiringInDays: filters.expiringInDays || undefined,
            search: filters.search || undefined,
        }));
        setIsLoading(false);
    }, [merchantFilter, page, limit, filters]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (!merchantFilter) { setTypes([]); return; }
        (async () => setTypes(await getMembershipTypes({ merchantId: merchantFilter })))();
    }, [merchantFilter]);

    const tiles = useMemo(() => {
        const rows = data.rows || [];
        return {
            total: data.total || 0,
            // Counted off the page, so it is labelled as such rather than pretending to
            // be a whole-population figure the endpoint never returned.
            activeOnPage: rows.filter((r) => r.status === "active").length,
            stoppedOnPage: rows.filter((r) => ["suspended", "revoked"].includes(r.status)).length,
            tiers: types.length,
        };
    }, [data, types]);

    const exportPage = () => {
        exportToCsv(`memberships-page-${page}.csv`, data.rows || [], [
            { key: "membershipRef", label: "Card number" },
            { key: (r) => memberName(r), label: "Member" },
            { key: (r) => r.user?.phoneNo || "", label: "Phone" },
            { key: (r) => r.membershipType?.name || "", label: "Tier" },
            { key: (r) => formatDiscount(r.membershipType?.discountType, r.membershipType?.discountValue), label: "Discount" },
            { key: "status", label: "Status" },
            { key: "employeeId", label: "Employee ID" },
            { key: (r) => (r.issuedDate ? new Date(r.issuedDate).toISOString().slice(0, 10) : ""), label: "Issued" },
            { key: (r) => (r.expiryDate ? new Date(r.expiryDate).toISOString().slice(0, 10) : ""), label: "Expires" },
            // No verification column: nothing read the field, so exporting it invited
            // somebody to filter a spreadsheet on a value that meant nothing.
        ]);
    };

    /** Per-member caps, when they differ from the tier's. */
    const overrideSummary = (m) => {
        const parts = [
            m.maxOrderValue != null && `max SAR ${m.maxOrderValue}`,
            m.maxDailyOrders != null && `${m.maxDailyOrders}/day`,
            m.maxMonthlyOrders != null && `${m.maxMonthlyOrders}/mo`,
        ].filter(Boolean);
        return parts.length ? parts.join(" · ") : null;
    };

    return (
        <Fragment>
            <Breadcrumb title="Members" parent="Membership" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile label="Members Found" value={tiles.total} sub={filters.search || filters.status ? "Matching your filters" : "All members"} icon="👥" gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`} delay={0} />
                        <Tile label="Active On This Page" value={tiles.activeOnPage} sub={`Of ${(data.rows || []).length} shown`} icon="✓" gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`} delay={80} />
                        <Tile label="Stopped On This Page" value={tiles.stoppedOnPage} sub="Suspended or revoked" icon="⏸" gradient={`linear-gradient(135deg, ${C.orange}, #FFB347)`} delay={160} />
                        <Tile label="Membership Types" value={tiles.tiers} sub="Configured for this merchant" icon="🎖️" gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`} delay={240} />
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", flexWrap: "wrap", gap: 10 }}>
                                <span>👥 Members <span style={{ color: C.textLight, fontWeight: 500, marginLeft: 8 }}>· {tiles.total} total</span></span>
                                <div style={{ display: "flex", gap: 8 }}>
                                    <button onClick={exportPage} style={ghostButton} disabled={!(data.rows || []).length}>⬇ Export page</button>
                                    {merchantFilter && (
                                        <button onClick={() => setShowAssign(true)} style={buttonStyle}>+ Assign Membership</button>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                            <input
                                style={{ ...inputStyle, minWidth: 260 }}
                                placeholder="Card number, employee ID, name, phone or email"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            <select
                                style={inputStyle}
                                value={filters.status}
                                onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value })); setPage(1); }}
                            >
                                <option value="">Any status</option>
                                {Object.entries(STATUS_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            <select
                                style={inputStyle}
                                value={filters.membershipTypeId}
                                onChange={(e) => { setFilters((f) => ({ ...f, membershipTypeId: e.target.value })); setPage(1); }}
                            >
                                <option value="">Any tier</option>
                                {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                            <select
                                style={inputStyle}
                                value={filters.expiringInDays}
                                onChange={(e) => { setFilters((f) => ({ ...f, expiringInDays: e.target.value })); setPage(1); }}
                            >
                                <option value="">Any expiry</option>
                                <option value="7">Expiring within 7 days</option>
                                <option value="30">Expiring within 30 days</option>
                                <option value="90">Expiring within 90 days</option>
                            </select>
                            {(filters.status || filters.membershipTypeId || filters.expiringInDays || searchInput) && (
                                <button
                                    style={ghostButton}
                                    onClick={() => { setFilters({ status: "", membershipTypeId: "", expiringInDays: "", search: "" }); setSearchInput(""); setPage(1); }}
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        {!merchantFilter ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant to see its members.
                            </div>
                        ) : isLoading ? (
                            <Loader />
                        ) : (data.rows || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                No members match. {filters.search ? "Try a different search." : "Click + Assign Membership to add one."}
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: "auto" }}>
                                    <table style={tbl.table}>
                                        <thead>
                                            <tr>
                                                <th style={tbl.th}>Card</th>
                                                <th style={tbl.th}>Member</th>
                                                <th style={tbl.th}>Tier</th>
                                                <th style={tbl.th}>Own Limits</th>
                                                <th style={tbl.th}>Issued</th>
                                                <th style={tbl.th}>Expires</th>
                                                <th style={tbl.th}>Status</th>
                                                <th style={tbl.th}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.rows || []).map((m) => {
                                                const overrides = overrideSummary(m);
                                                const expired = m.expiryDate && new Date(m.expiryDate) < new Date();
                                                return (
                                                    <tr key={m.id} className="rpt-row">
                                                        <td style={{ ...tbl.td, fontWeight: 600, fontFamily: "monospace", color: C.text }}>
                                                            {m.membershipRef}
                                                            {m.qrStatus === "disabled" && (
                                                                <div style={{ fontSize: 10, color: C.coral, fontWeight: 600 }}>QR DISABLED</div>
                                                            )}
                                                        </td>
                                                        <td style={tbl.td}>
                                                            <div style={{ fontWeight: 600, color: C.text }}>{memberName(m)}</div>
                                                            <div style={{ fontSize: 11, color: C.textLight }}>
                                                                {m.user?.phoneNo || "—"}
                                                                {m.employeeId && ` · #${m.employeeId}`}
                                                            </div>
                                                        </td>
                                                        <td style={tbl.td}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                <span style={{
                                                                    width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                                                                    background: m.membershipType?.cardColour || C.textLight,
                                                                }} />
                                                                <div>
                                                                    <div style={{ fontWeight: 500 }}>{m.membershipType?.name || "—"}</div>
                                                                    <div style={{ fontSize: 11, color: C.textLight }}>
                                                                        {formatDiscount(m.membershipType?.discountType, m.membershipType?.discountValue)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ ...tbl.td, fontSize: 12, color: C.textMed }}>
                                                            {overrides
                                                                ? <Badge color={C.purple} bg={C.purpleLight}>{overrides}</Badge>
                                                                : <span style={{ color: C.textLight }}>Tier defaults</span>}
                                                        </td>
                                                        <td style={{ ...tbl.td, color: C.textMed, fontSize: 12 }}>{fmtDate(m.issuedDate)}</td>
                                                        <td style={{ ...tbl.td, fontSize: 12, color: expired ? C.coral : C.textMed }}>
                                                            {m.expiryDate ? fmtDate(m.expiryDate) : <span style={{ color: C.textLight }}>Never</span>}
                                                        </td>
                                                        <td style={tbl.td}><StatusBadge status={m.status} /></td>
                                                        <td style={tbl.td}>
                                                            <button
                                                                onClick={() => navigate(`/membership/members/${m.id}`)}
                                                                style={{ ...ghostButton, padding: "6px 14px", fontSize: 12 }}
                                                            >
                                                                Open
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                <TablePagination
                                    total={data.total || 0}
                                    page={page}
                                    limit={limit}
                                    onPageChange={setPage}
                                    onLimitChange={(l) => { setLimit(l); setPage(1); }}
                                />
                            </>
                        )}
                    </RCard>
                </div>
            </Container>

            {showAssign && (
                <AssignMembershipModal
                    merchantId={merchantFilter}
                    types={types}
                    onClose={() => setShowAssign(false)}
                    onSaved={(membership) => {
                        setShowAssign(false);
                        if (membership?.id) navigate(`/membership/members/${membership.id}`);
                        else fetchData();
                    }}
                    // A batch has no single new member to open, so it stays on the list and
                    // reloads it — the admin has just read the report and wants to see the
                    // people it created.
                    onBulkDone={() => {
                        setShowAssign(false);
                        fetchData();
                    }}
                />
            )}
        </Fragment>
    );
};

export default MembershipList;
